/**
 * Better-Auth client: configured auth plugins, session atom, and the
 * `useSession` hook used app-wide.
 *
 * Wraps Better-Auth's `createAuthClient` with PSI-specific plugins. The
 * `bearer` plugin moves the session token through `localStorage` and an
 * `Authorization` header for environments without cookies (native webview,
 * cross-origin embed); the `firebase` plugin runs Google sign-in and sends the
 * Firebase ID token as the `Authorization` header, which is how a Firebase
 * silo authenticates every request to the server; the `testing`
 * plugin exposes a `/sign-in/testing` endpoint that lets E2E tests mint a
 * session without going through a real IdP. Which plugins load is decided once
 * at module load via `EXPO_PUBLIC_DATABASE_ADAPTER` — Firebase silos use bearer
 * + firebase + testing, MongoDB silos use bearer + testing only. The exported
 *   `getUser` / `onUserChanged` are deprecated in favor of `useSession` but
 *   still wired up because `Datastore` boots before any React tree is mounted.
 */

import type { BetterAuthClientPlugin, SessionQueryParams, StrictEndpoint, ZodAny, ZodObject, ZodOptional, ZodString } from 'better-auth';
import type { BetterFetchError, PreinitializedWritableAtom } from 'better-auth/react';
import { createAuthClient } from 'better-auth/react';
import { GoogleAuthProvider, signInWithCustomToken, signInWithPopup, signOut } from 'firebase/auth';
import localspace, { syncPlugin } from 'localspace';
import { use } from 'react';
import { getFirebaseAuth } from 'system/firebase-init';

import { getApiBaseUrl } from './util';

const localStorage = localspace.createInstance({
    name: 'psi-local-storage',
    plugins: [syncPlugin({ channelName: 'psi-local-storage-sync' })],
});

const GET_SESSION_URL = `${getApiBaseUrl()}auth/get-session`;
const SIGN_OUT_URL = `${getApiBaseUrl()}auth/sign-out`;

const BEARER_TOKEN_KEY = 'bearer-token';

export const usesFirebaseAuth = process.env.EXPO_PUBLIC_DATABASE_ADAPTER !== 'mongodb';

/**
 * Attach the stored bearer token as an `Authorization` header, if one exists.
 * Shared by the `bearer` plugin's own `signRequest` and, as a fallback, by the
 * `firebase` plugin's — see the comment on the latter for why it needs one.
 */
async function signRequestWithBearerToken(headers: Headers): Promise<void> {
    const token = await localStorage.getItem<string>(BEARER_TOKEN_KEY);
    if (token) headers.set('authorization', `Bearer ${token}`);
}

const bearer = () => {
    return {
        id: 'bearer',
        getActions: () => ({
            /**
             * Attach this plugin's authentication header(s) to the given
             * `Headers` object before an outgoing API request. Used by
             * non-`authClient.$fetch` callers (e.g. the oRPC `RPCLink`'s
             * `fetch` hook) so they don't have to know what auth scheme
             * is in play — the plugin owns it.
             */
            signRequest: signRequestWithBearerToken,
        }),
        fetchPlugins: [
            {
                id: 'bearer',
                name: 'bearer',
                hooks: {
                    // Deliberately does not set `ctx.credentials = 'omit'` on
                    // get-session: Better Auth already resolves whichever of
                    // the cookie or the bearer header actually validates,
                    // and a stale one does not shadow a valid other one
                    // (verified against the live server: valid cookie +
                    // invalid bearer, and invalid cookie + valid bearer,
                    // both return the session). Omitting the cookie only
                    // removed one of the two ways this call could succeed —
                    // e.g. right after an OAuth callback, when a valid
                    // cookie exists but no bearer token has been stored yet
                    // (the token only comes from a `$fetch` response header,
                    // which a top-level IdP redirect never produces), or
                    // when a stored bearer token has gone stale while the
                    // cookie is still fresh. Both cases left the client
                    // unable to recognize a session the server already has.
                    onRequest: async (ctx) => {
                        await signRequestWithBearerToken(ctx.headers);
                    },
                    onResponse: async (ctx) => {
                        const bearerToken = ctx.response.headers.get('set-auth-token');
                        if (bearerToken) {
                            await localStorage.setItem<string>(BEARER_TOKEN_KEY, bearerToken);
                        }

                        const status = ctx.response.status;
                        const url = String(ctx.request.url);
                        if (status === 401) {
                            await localStorage.removeItem(BEARER_TOKEN_KEY);
                        } else if (url === GET_SESSION_URL) {
                            const response = ctx.response.clone();
                            const data = await response.json();
                            if (data === null) {
                                await localStorage.removeItem(BEARER_TOKEN_KEY);
                            }
                        } else if (url === SIGN_OUT_URL) {
                            await localStorage.removeItem(BEARER_TOKEN_KEY);
                        }
                    },
                },
            },
        ],
    } satisfies BetterAuthClientPlugin;
};

const firebase = () => {
    return {
        id: 'firebase',
        getActions: () => ({
            signIn: {
                google: async () => {
                    const auth = getFirebaseAuth();
                    const provider = new GoogleAuthProvider();
                    await signInWithPopup(auth, provider);
                },
            },
            /**
             * Attach the current Firebase ID token (auto-refreshed by the
             * Firebase SDK; 1-hour expiry) as a Bearer header, falling back to
             * the bearer token when there's no signed-in Firebase user, or
             * fetching its token fails (e.g. a login flow, such as PKCE, that
             * never established a Firebase session, or a refresh error).
             * Firebase mode loads this plugin after `bearer()`, so
             * better-auth keeps this action over `bearer`'s own `signRequest`
             * wholesale rather than chaining them — unlike the two plugins'
             * `onRequest` fetch hooks below, which both always run, this
             * fallback has to be inlined here to get the same effect.
             */
            signRequest: async (headers: Headers): Promise<void> => {
                const auth = getFirebaseAuth();
                await auth.authStateReady();
                const token = await auth.currentUser?.getIdToken().catch(() => undefined);
                if (token) {
                    headers.set('authorization', `Bearer ${token}`);
                } else {
                    await signRequestWithBearerToken(headers);
                }
            },
        }),
        fetchPlugins: [
            {
                id: 'firebase',
                name: 'firebase',
                hooks: {
                    // See the bearer plugin's `onRequest` above: deliberately
                    // does not omit the cookie on get-session either.
                    onRequest: async (ctx) => {
                        const auth = getFirebaseAuth();
                        await auth.authStateReady();

                        const firebaseToken = await auth.currentUser?.getIdToken();
                        if (firebaseToken) {
                            ctx.headers.set('authorization', `Bearer ${firebaseToken}`);
                        }
                    },
                    onResponse: async (ctx) => {
                        const auth = getFirebaseAuth();

                        const firebaseToken = ctx.response.headers.get('set-firebase-token');
                        if (firebaseToken) {
                            await signInWithCustomToken(auth, firebaseToken);
                        }

                        const status = ctx.response.status;
                        const url = String(ctx.request.url);
                        if (status === 401) {
                            await signOut(auth);
                        } else if (url === GET_SESSION_URL) {
                            const response = ctx.response.clone();
                            const data = await response.json();
                            if (data === null) {
                                await signOut(auth);
                            }
                        } else if (url === SIGN_OUT_URL) {
                            await signOut(auth);
                        }
                    },
                },
            },
        ],
    } satisfies BetterAuthClientPlugin;
};

const testing = () => {
    return {
        id: 'testing',
        // TODO: Reference the server plugin once server and client can share
        // code.
        $InferServerPlugin: {} as {
            id: 'testing';
            endpoints: {
                signInTesting: StrictEndpoint<
                    '/sign-in/testing',
                    {
                        method: 'POST';
                        body: ZodObject<{
                            id: ZodString;
                            data: ZodOptional<ZodAny>;
                        }>;
                    },
                    {}
                >;
            };
        },
    } satisfies BetterAuthClientPlugin;
};

const authPlugins = usesFirebaseAuth ? [bearer(), firebase(), testing()] : [bearer(), testing()];

export const authClient = createAuthClient({
    baseURL: `${getApiBaseUrl()}auth`,
    plugins: process.env.STORYBOOK ? [] : authPlugins,
});

type Session = typeof authClient.$Infer.Session.session;
export type User = typeof authClient.$Infer.Session.user;

/**
 * The Better Auth (internal) session atom.
 * @deprecated in favor of the `useSession` hook.
 */
const sessionAtom = authClient.$store.atoms.session as PreinitializedWritableAtom<{
    data: null | { user: User; session: Session };
    error: null | BetterFetchError;
    isPending: boolean;
    isRefetching: boolean;
    refetch: (queryParams?: { query?: SessionQueryParams } | undefined) => Promise<void>;
}>;

/**
 * Get the currently authenticated user, if any.
 * @deprecated in favor of the `useSession` hook.
 * @returns The current user, null if not authenticated, or undefined if still
 * initializing
 */
export function getUser(): User | null | undefined {
    return hasInitialFetchSettled() ? (sessionAtom.get().data?.user ?? null) : undefined;
}

let initialFetchHasSettled = false;
let initialFetchSettled: Promise<void> | undefined;

/**
 * Whether the session's first fetch has settled, whatever its result. Better
 * Auth sets `isPending` again on every fetch while signed out, so only the
 * first time it clears tells a cold load from a signed-out reader.
 */
function hasInitialFetchSettled(): boolean {
    if (!initialFetchHasSettled && !sessionAtom.get().isPending) {
        initialFetchHasSettled = true;
    }
    return initialFetchHasSettled;
}

/**
 * Resolves once the session's first fetch settles. It's created once, because
 * React's `use` tracks a promise by identity. It starts listening on first use
 * rather than at module load, since listening mounts the atom and starts a
 * session fetch.
 */
function waitForInitialFetch(): Promise<void> {
    initialFetchSettled ??= new Promise((resolve) => {
        const unlisten = sessionAtom.listen((session) => {
            if (session.isPending) {
                return;
            }

            initialFetchHasSettled = true;
            unlisten();
            resolve();
        });
    });
    return initialFetchSettled;
}

/**
 * `useSession`, suspending until the session's first fetch settles, so the
 * caller never sees a session that hasn't loaded yet.
 *
 * A caller can't tell a failed first fetch from a signed-out reader. The
 * failed fetch counts as settled, and the session reads as signed out until a
 * later fetch succeeds. Better Auth fetches again when the page becomes
 * visible or the browser comes back online, and on any sign-in or sign-out.
 *
 * In a server render the hook suspends forever, since Better Auth fetches the
 * session only in the browser. To render on the server, the hook would have
 * to throw there before it touches the session atom, which makes React send
 * the fallback and render the boundary again in the browser.
 */
// TODO: Use Better Auth's useSession once it suspends
// (better-auth/better-auth#11219).
export function useSuspenseSession() {
    const session = useSession();

    if (!hasInitialFetchSettled()) {
        use(waitForInitialFetch());
    }

    return session;
}

/**
 * Register a callback to be notified when the auth state changes.
 * @deprecated in favor of the `useSession` hook.
 * @param callback - Function to call when auth state changes
 * @returns Cleanup function to unregister the callback
 */
export function onUserChanged(callback: (user: User | null) => void): () => void {
    return sessionAtom.subscribe((session) => {
        if (!session.isPending && !session.isRefetching) {
            callback(session.data?.user ?? null);
        }
    });
}

export const { useSession } = authClient;
